import { useContext, useEffect, useState, useMemo } from 'react';
import { Breadcrumb } from 'react-bootstrap';
import GridLayout from "react-grid-layout";
import { withTranslation } from 'react-i18next';
import { LoaderContext, RegulationContext } from '../../../App';
import { GraphLayoutType, LS_AUTHORITY_KEY, currentYear, startYear } from '../../../core/constants/common';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import GHGEmissionService from '../../../core/service/GHGEmissionService';
import CompanyGraphCard from '../CompanyDashboardGraph/CompanyGraphCard';
import Utils from '../../../core/utilities/Utils';

const GHGOverview = ({ t }) => {
 
  const { setLoaderDisplay } = useContext(LoaderContext);
  const { glbRegulation } = useContext(RegulationContext);
  const [filter, setFilter] = useState({ year: new Date().getFullYear(), type: 'ALL' });
  const [periodBasedData, setPeriodBasedData] = useState({});
  const [typeBasedData, setTypeBasedData] = useState({});
  const [summaryData, setSummaryData] = useState({});
  const [renderedOn, setRenderedOn] = useState(0);
  const [scopeGraphs, setScopeGraphs] = useState([]);
  const [layoutConfig, setLayoutConfig] = useState({});
  const [containerWidth, setContainerWidth] = useState(window.innerWidth);

  // Initialize localStorage data synchronously
  const [companyId] = useState(() => {
    try {
      const lsDat = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
      return lsDat?.profile?.company?.id || null;
    } catch {
      return null;
    }
  });

  // Extract primitive value for stable dependency tracking
  const regulationId = glbRegulation?.id;

  // Memoize year options to avoid recreating on every render
  const yearOptions = useMemo(() => {
    const options = [];
    for (let i = currentYear; i >= startYear; i--) {
      options.push(<option value={i} key={i}>{i}</option>);
    }
    return options;
  }, []);

  // Memoize grid layout width calculation
  const gridLayoutWidth = useMemo(() => {
    return containerWidth <= 780 ? containerWidth - 70 : containerWidth - 240;
  }, [containerWidth]);

  useEffect(() => {
    const handleResize = () => {
      setContainerWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!regulationId) return;

    if (filter.type === 'ALL') {
      getSummary(filter.year);
      getByPeriod(filter.year);
      getByGHGType(filter.year);
    } else {
      getScopeGraphConfig(filter.type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regulationId, filter.year, filter.type]);

  useEffect(() => {
    getGraphProps();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getSummary = (filteredYear) => {
    GHGEmissionService.getEmissionSummary(glbRegulation.id, `${filteredYear}-01-01`)
      .then(res => {
        if (res.status !== 200) return;

        setSummaryData(res.data.data);
        setRenderedOn(Date.now());
      }).catch(() => {
        // Silently handle errors
      });
  };

  const getByPeriod = (filteredYear) => {
    GHGEmissionService.getEmissionByPeriod(glbRegulation.id, `${filteredYear}-01-01`)
      .then(res => {
        if (res.status !== 200) return;

        setPeriodBasedData(res.data.data);
        setRenderedOn(Date.now());
      }).catch(() => {
        // Silently handle errors
      });
  };

  const getByGHGType = (filteredYear) => {
    GHGEmissionService.getEmissionByGHGType(glbRegulation.id, `${filteredYear}-01-01`)
      .then(res => {
        if (res.status !== 200) return;

        setTypeBasedData(res.data.data);
        setRenderedOn(Date.now());
      }).catch(() => {
        // Silently handle errors
      });
  };

  const renderHeader = () => {
    return (
      <div className="List_header pb-0">
        <Breadcrumb className='h5'>
          <Breadcrumb.Item >{t("GHGEmissions.label")}</Breadcrumb.Item>
          <Breadcrumb.Item active>{t("Overview.label")}</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex justify-content-end align-items-center header-actions'>
          <select className="form-select  w-auto" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
            <option value={GraphLayoutType.SCOPE_1.name}>{t("Scope1.label")}</option>
            <option value={GraphLayoutType.SCOPE_2.name}>{t("Scope2.label")}</option>
            <option value={GraphLayoutType.SCOPE_3.name}>{t("Scope3.label")}</option>
          </select>
          <select className="form-select  w-auto" value={filter.year}
            onChange={(e) => setFilter({ ...filter, year: e.target.value })}>
            {yearOptions}
          </select>
        </div>
      </div>
    )
  };

  const getTotalScope123Data = () => {
    return {
      type: "BAR",
      properties: {
        title: t('TotalScopeEm.label'),
        description: t('TotalScopeEm.label'),
        options: {
          timeInterval: "YEARLY",
          legendPosition: "BOTTOM CENTER",
          axisScale: "",
          yearOffset: 1
        },
        labels: {
          xAxis: "MONTH",
          yAxis: "VALUE"
        },
        localizedContents: []
      },
      dataPoints: [
        {
          xValue: "",
          order: 1,
          yValues: [
            {
              label: t('Scope1.label'),
              value: summaryData.scope1?.toFixed(2) ?? 0,
              order: 1,
              localizedContents: []
            },
            {
              label: t('Scope2.label'),
              value: summaryData.scope2?.toFixed(2) ?? 0,
              order: 2,
              localizedContents: []
            },
            {
              label: t('Scope3.label'),
              value: summaryData.scope3?.toFixed(2) ?? 0,
              order: 3,
              localizedContents: []
            },
          ]
        },

      ],
      dataPointOptions: [
        {
          label: t('Scope1.label'),
          unitOfMeasure: "tCO2e",
          position: "",
          color: "ROYAL_BLUE",
          style: "SOLID",
          yearOffset: 0
        },
        {
          label: t('Scope2.label'),
          unitOfMeasure: "tCO2e",
          position: "",
          color: "LAVENDER_GREY",
          style: "SOLID",
          yearOffset: 0
        },
        {
          label: t('Scope3.label'),
          unitOfMeasure: "tCO2e",
          position: "",
          color: "EMERALD_GREEN",
          style: "SOLID",
          yearOffset: 0
        },
      ],
      footers: [],
      gridLines: false,
      overrideLegendPosition: true
    }
  };

  const getEmissionsByPeriodData = () => {
    const months = [
      { name: 'January', monthCode: '01' },
      { name: 'February', monthCode: '02' },
      { name: 'March', monthCode: '03' },
      { name: 'April', monthCode: '04' },
      { name: 'May', monthCode: '05' },
      { name: 'June', monthCode: '06' },
      { name: 'July', monthCode: '07' },
      { name: 'August', monthCode: '08' },
      { name: 'September', monthCode: '09' },
      { name: 'October', monthCode: '10' },
      { name: 'November', monthCode: '11' },
      { name: 'December', monthCode: '12' },
    ];

    const scopeLabels = [
      { key: 'Scope1.label', field: 'scope1', order: 1 },
      { key: 'Scope2.label', field: 'scope2', order: 2 },
      { key: 'Scope3.label', field: 'scope3', order: 3 },
    ];

    const dataPoints = months.map((month, index) => {
      const dateKey = `${filter.year}-${month.monthCode}-01`;
      const periodData = periodBasedData[dateKey] || {};
      return {
        xValue: month.name,
        order: index + 1,
        yValues: scopeLabels.map(scope => ({
          label: t(scope.key),
          value: periodData[scope.field]?.toFixed(2) || 0,
          order: scope.order,
          localizedContents: [],
        })),
      };
    });

    return {
      "type": "STACKED BAR",
      "properties": {
        "title": t('EmissionsByPeriod.label'),
        "description": t('EmissionsByPeriod.label'),
        "options": {
          "timeInterval": "MONTHLY",
          "legendPosition": "BOTTOM CENTRE",
          "axisScale": "LINEAR",
          "yearOffset": 0
        },
        "labels": {
          "xAxis": "VALUE",
          "yAxis": "MONTH"
        },
        "localizedContents": []
      },
      "dataPoints": dataPoints,
      "dataPointOptions": [
        {
          "label": t('Scope1.label'),
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "ROYAL_BLUE",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": t('Scope2.label'),
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "LAVENDER_GREY",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": t('Scope3.label'),
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "EMERALD_GREEN",
          "style": "SOLID",
          "yearOffset": 0
        },
      ],
      "footers": [],
      "gridLines": false,
      "overrideLegendPosition": true
    };
  };

  const getEmissionsByGHGTypeData = () => {
    return {
      "type": "PIE",
      "properties": {
        "title": t('EmissionsByGHG.label'),
        "description": t('EmissionsByGHG.label'),
        "options": {
          "timeInterval": "YEARLY",
          "legendPosition": "BOTTOM RIGHT",
          "axisScale": "",
          "yearOffset": 0
        },
        "labels": {
          "xAxis": "VALUE",
          "yAxis": "TIME"
        },
        "localizedContents": []
      },
      "dataPoints": [
        {
          "xValue": filter.year,
          "order": 1,
          "yValues": [
            {
              "label": "CO2",
              "value": typeBasedData.co2 ?? 150, // Dummy data
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "CH4",
              "value": typeBasedData.ch4 ?? 75, // Dummy data
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "N2O",
              "value": typeBasedData.n2o ?? 200, // Dummy data
              "order": 3,
              "localizedContents": []
            },
            {
              "label": "SF6",
              "value": typeBasedData.sf6 ?? 75, // Dummy data
              "order": 4,
              "localizedContents": []
            },
            {
              "label": "Others",
              "value": typeBasedData.others ?? 250, // Dummy data
              "order": 5,
              "localizedContents": []
            },
          ]
        }
      ],
      "dataPointOptions": [
        {
          "label": "CO2",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "ROYAL_BLUE",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "CH4",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "ORANGE",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "N2O",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "SILVER_GREY",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "SF6",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "EMERALD_GREEN",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "Others",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "LAVENDER_GREY",
          "style": "SOLID",
          "yearOffset": 0
        },
      ],
      "footers": [],
      "gridLines": false,
      "overrideLegendPosition": true
    };
  };

  const getTotalEmissionsData = () => {
    const months = [
      { name: 'Jan', monthCode: '01' },
      { name: 'Feb', monthCode: '02' },
      { name: 'Mar', monthCode: '03' },
      { name: 'Apr', monthCode: '04' },
      { name: 'May', monthCode: '05' },
      { name: 'Jun', monthCode: '06' },
      { name: 'Jul', monthCode: '07' },
      { name: 'Aug', monthCode: '08' },
      { name: 'Sep', monthCode: '09' },
      { name: 'Oct', monthCode: '10' },
      { name: 'Nov', monthCode: '11' },
      { name: 'Dec', monthCode: '12' },
    ];
    const dataPoints = months.map((month, index) => {
      const dateKey = `${filter.year}-${month.monthCode}-01`;
      const totalValue = periodBasedData[dateKey]?.total?.toFixed(2) ?? 0;
      return {
        xValue: month.name,
        order: index + 1,
        yValues: [
          {
            label: "Total",
            value: totalValue,
            order: 1,
            localizedContents: []
          }
        ],
      };
    });

    return {
      "type": "LINE",
      "properties": {
        "title": t('TotalEmInorg.label'),
        "description": t('TotalEmInorg.label'),
        "options": {
          "timeInterval": "MONTHLY",
          "legendPosition": "TOP CENTRE",
          "axisScale": "LINEAR",
          "yearOffset": 0
        },
        "labels": {
          "xAxis": "MONTH",
          "yAxis": "VALUE"
        },
        "localizedContents": []
      },
      "dataPoints": dataPoints, // Use the dynamically generated dataPoints array
      "dataPointOptions": [
        {
          "label": "Total",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "ROYAL_BLUE",
          "style": "SOLID",
          "yearOffset": 0
        },
      ],
      "footers": [],
      "gridLines": false,
      "fill": true,
      "hidePoints": true,
      "fillAsGradient": true,
      "hideLegend": true
    };
  };

  const getEmissionsByCategoryData = () => {
    // Dummy data
    return {
      "type": "HORIZONTAL BAR",
      "properties": {
        "title": "Emissions By Category",
        "description": "Emissions By Category",
        "options": {
          "timeInterval": "YEARLY",
          "legendPosition": "TOP CENTRE",
          "axisScale": "",
          "yearOffset": 2
        },
        "labels": {
          "xAxis": "YEAR",
          "yAxis": "VALUE"
        },
        "localizedContents": []
      },      
      "dataPoints": [
        {
          "xValue": "Downstream Leased Assets",
          "order": 8,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 450,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Downstream Transportation and Distribution",
          "order": 7,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 300,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Upstream Leased Assets",
          "order": 6,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 550,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Fuel and Energy Related Activities Not Included in Scope 1 or Scope 2 Upstream Transportation and Distribution",
          "order": 5,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 55,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Employee Commuting",
          "order": 4,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 125,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Purchased Goods and Services",
          "order": 3,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 410,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Business Travel",
          "order": 2,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 250,
              "order": 1,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Waste Generated in Operations",
          "order": 1,
          "yValues": [
            {
              "label": "Scope 3",
              "value": 175,
              "order": 1,
              "localizedContents": []
            }
          ]
        }
      ],
      "dataPointOptions": [
        {
          "label": "Scope 3",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "EMERALD_GREEN",
          "style": "SOLID",
          "yearOffset": 0
        },
      ],
      "footers": [],
      "gridLines": false,
      "overrideLegendPosition": true
    };
  };

  const getEmissionsInEntitiesData = () => {
    // Dummy data
    return {
      "type": "HORIZONTAL STACKED BAR",
      "properties": {
        "title": "Top 5 Emissions in Entities",
        "description": "Top 5 Emissions in Entities",
        "options": {
          "timeInterval": "YEARLY",
          "legendPosition": "BOTTOM CENTRE",
          "axisScale": "LINEAR",
          "yearOffset": 0
        },
        "labels": {
          "xAxis": "YEAR",
          "yAxis": "VALUE"
        },
        "localizedContents": []
      }, 
      "dataPoints": [
        {
          "xValue": "Location 1",
          "order": 6,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 40,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 15,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 45,
              "order": 3,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Location 2",
          "order": 5,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 25,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 25,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 50,
              "order": 3,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Location 4",
          "order": 4,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 65,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 15,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 20,
              "order": 3,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Location 6",
          "order": 3,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 35,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 35,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 30,
              "order": 3,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Location 3",
          "order": 2,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 80,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 10,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 10,
              "order": 3,
              "localizedContents": []
            }
          ]
        },
        {
          "xValue": "Location 5",
          "order": 1,
          "yValues": [
            {
              "label": "Scope 1",
              "value": 60,
              "order": 1,
              "localizedContents": []
            },
            {
              "label": "Scope 2",
              "value": 15,
              "order": 2,
              "localizedContents": []
            },
            {
              "label": "Scope 3",
              "value": 25,
              "order": 3,
              "localizedContents": []
            }
          ]
        },        
      ],
      "dataPointOptions": [
        {
          "label": "Scope 1",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "ROYAL_BLUE",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "Scope 2",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "LAVENDER_GREY",
          "style": "SOLID",
          "yearOffset": 0
        },
        {
          "label": "Scope 3",
          "unitOfMeasure": "tCO2e",
          "position": "",
          "color": "EMERALD_GREEN",
          "style": "SOLID",
          "yearOffset": 0
        },
      ],
      "footers": [],
      "gridLines": false,
      "overrideLegendPosition": true
    };
  };

  const getGraphProps = () => {
    DashGraphConfigService.getProps('GRAPH').then(res => {
      if (res.status !== 200) return;
      // Graph props are fetched but not currently used in the component
      // Keeping this function for potential future use
    }).catch(() => {
      // Silently handle errors
    });
  };

  const getScopeGraphConfig = (scope) => {
    setLoaderDisplay(true);
    const graphLayoutType = parseLayoutTypeFromFilter(scope);
    DashGraphConfigService.getCompanyGraphs(
      companyId, glbRegulation.id,
      'ENVIRONMENTAL', glbRegulation?.name,
      null, null, true, false, graphLayoutType?.text, '', graphLayoutType?.value
    ).then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200) return;

      const graphs = res.data?.graphs ?? [];
      setScopeGraphs(graphs);
      setLayoutConfig({
        id: res.data?.layout?.id,
        layout: Utils.setupDynamicGraphLayoutConfigFor({
          graphs: graphs,
          lData: res.data?.layout,
          activeLayoutConfig: layoutConfig
        })
      });
      setRenderedOn(Date.now());
    }).catch(() => {
      setLoaderDisplay(false);
    });
  };

  const parseLayoutTypeFromFilter = (f) => {
    switch (f) {
      case 'All':
      case GraphLayoutType.OVERVIEW.name: return GraphLayoutType.OVERVIEW;
      case GraphLayoutType.SCOPE_1.name: return GraphLayoutType.SCOPE_1;
      case GraphLayoutType.SCOPE_2.name: return GraphLayoutType.SCOPE_2;
      case GraphLayoutType.SCOPE_3.name: return GraphLayoutType.SCOPE_3;
      default: return { name: '', value: '' };
    }
  };

  const renderOverviewGraphs = () => {
    return (
      <div>
        <div className="row m-0 my-1">
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph1`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('TotalEmissions.label')} (tCO2e)`, data: summaryData.total?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph2`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('Scope1.label')} (tCO2e)`, data: summaryData.scope1?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph3`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('Scope2.label')} (tCO2e)`, data: summaryData.scope2?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph4`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('Scope3.label')} (tCO2e)`, data: summaryData.scope3?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph5`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('RenewableEmissions.label')} (tCO2e)`, data: summaryData.renewable?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-2 col-md-2 p-2 d-flex">
            <CompanyGraphCard
              key={`ghgEmGraph6`}
              renderedOn={renderedOn}
              config={{ type: 'KPI', label: `${t('NonRenewableEmissions.label')} (tCO2e)`, data: summaryData.nonRenewable?.toFixed(2) ?? 0 }}
              graphType={'GHG_EMISSION'} />
          </div>
        </div>
        <div className="row m-0 my-1">
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph7`}
              renderedOn={renderedOn}
              config={getTotalEmissionsData()}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph8`}
              renderedOn={renderedOn}
              config={getTotalScope123Data()}
              graphType={'GHG_EMISSION'} />
          </div>
        </div>
        <div className="row m-0 my-1">
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph9`}
              renderedOn={renderedOn}
              config={getEmissionsByPeriodData()}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph10`}
              renderedOn={renderedOn}
              config={getEmissionsByGHGTypeData()}
              graphType={'GHG_EMISSION'} />
          </div>
        </div>
        <div className="row m-0 my-1">
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph11`}
              renderedOn={renderedOn}
              config={getEmissionsByCategoryData()}
              graphType={'GHG_EMISSION'} />
          </div>
          <div className="col-lg-6 col-md-6 p-2">
            <CompanyGraphCard
              key={`ghgEmGraph12`}
              renderedOn={renderedOn}
              config={getEmissionsInEntitiesData()}
              graphType={'GHG_EMISSION'} />
          </div>
        </div>
      </div>
    );
  };

  const renderScopeGraphs = () => {
    return (
      <GridLayout
        className="layout"
        layout={getFilteredLayoutForScopeGraphs()}
        cols={16}
        rowHeight={30}
        width={gridLayoutWidth}
        onLayoutChange={onLayoutChange}
        onResizeStop={handleResizeStop}
        onDragStop={handleDragStop}
        isResizable={true}
        isDraggable={true}
        draggableHandle=".drag-handle" // This class is used to specify the drag handle defined in CompanyGraphCard
        margin={[20, 20]} // Spacing between grid items
        containerPadding={[5, 10]}>
        {
          scopeGraphs?.filter(g => g.type !== 'KPI')?.map((graph, index) => {
            return (
              <div key={`graph-${graph.id}`}>{renderGraphColumn('esg', graph, index)}</div>
            );
          })
        }
        {
          scopeGraphs?.filter(g => g.type === 'KPI')?.map((graph, index) => {
            return (
              <div className="ESG_performance_ShortValuesBOx" key={`graph-${graph.id}`}>
                <CompanyGraphCard
                  key={`esgKPIGraph-${graph.id}-${index}`}
                  renderedOn={renderedOn}
                  year={filter.year}
                  config={graph} />
              </div>
            );
          })
        }
      </GridLayout>
    );
  }

  const renderGraphColumn = (type, graph, index) => {
    return (
      <div className="p-2 "
        key={`${type}Graph-${graph.id}-${index}`}
        id={`${type}Graph-${graph.id}-${index}`}>
        <CompanyGraphCard
          key={`${type}Graph-${graph.id}-${index}`}
          renderedOn={renderedOn}
          year={filter.year}
          config={graph} />
      </div>
    );
  };

  const getFilteredLayoutForScopeGraphs = () => {
    const graphIds = [];
    scopeGraphs?.forEach(g => graphIds.push(g.id));
    return layoutConfig?.layout?.filter(l => graphIds.indexOf(+(l.i.split('-')[1])) >= 0);
  };

  const handleResizeStop = (layout, oldItem, newItem) => {
    saveLayout(layout);
  };

  const handleDragStop = (layout, oldItem, newItem) => {
    saveLayout(layout);
  };

  const onLayoutChange = () => {
    // Layout change handler - currently no action needed
    // GridLayout passes newLayout parameter, but we don't need it here
    // Layout is saved via onResizeStop and onDragStop handlers instead
  };

  const saveLayout = (updatedLayout) => {
    const layout = [];
    updatedLayout?.forEach(l => {
      layout.push({
        i: +(l.i.split('-')[1]),
        x: l.x,
        y: l.y,
        w: l.w,
        h: l.h,
        minW: l.minW,
        minH: l.minH
      });
    });
    // Add other category layouts
    layoutConfig?.layout?.forEach(l => {
      const id = +(l.i.split('-')[1]);
      if (layout.findIndex(o => o.i === id) < 0) {
        layout.push({
          i: +(l.i.split('-')[1]),
          x: l.x,
          y: l.y,
          w: l.w,
          h: l.h,
          minW: l.minW,
          minH: l.minH
        });
      }
    });
    const graphLayoutType = parseLayoutTypeFromFilter(filter.type);
    // Special case for Overview: use "SCOPE_OVERVIEW" instead of "OVERVIEW"
    const layoutType = graphLayoutType.name === 'OVERVIEW' ? 'SCOPE_OVERVIEW' : graphLayoutType.name;
    console.log('GHGOverview saving layout with type:', layoutType, 'for filter.type:', filter.type);
    const lConfig = {
      ...layoutConfig,
      layout: layout,
      type: layoutType
    };
    setLoaderDisplay(true);
    DashGraphConfigService.saveGraphLayout(glbRegulation.id, lConfig).then(res => {
      setLoaderDisplay(false);
      if (res.status !== 200 && res.status !== 201) return;

    }).catch(() => {
      setLoaderDisplay(false);
    });
  };

  return (
    <div className='CompanyDashboard Standard_Dashboard_box'>
      {renderHeader()}
      {filter.type === 'ALL' ? renderOverviewGraphs() : renderScopeGraphs()}
    </div >
  )
};
export default withTranslation()(GHGOverview);